import { cn } from '@anytag/shared-utils';
import { useQueryHelper } from '@anytag/shared-hooks';
import { SupportedSocialMediaType } from '@anytag/shared-types';
import { useMarketplacePermissions, useUserRoles } from '@anytag/auth';
import { Button, Drawer, Form, RenderDataWithFallback } from '@anytag/shared-atoms';
import {
  WarningReason,
  MarketplaceDetail,
  useMarketplacePostQuery,
  MarketplaceCampaignPostStatus,
  useMarketplacePostHistoryQuery,
  useUpdateMarketplacePostStatusV2Mutation,
  MarketplaceCampaignPostStatusForAdvertiser,
  namedOperations
} from '@anytag/graphql';
import { useRejectMarketplacePost, useUpdateMarketplacePostsStatus } from '../hooks';
import { HistorySection, PostInfoSection, RequestModificationMessage } from './shared';

interface DefaultTemplateProps {
  postId: number;
  onClose: () => void;
  isAffiliateCampaign?: boolean;
  marketplaceId: MarketplaceDetail['id'];
}

export const DefaultTemplateComponent = ({
  postId,
  onClose,
  marketplaceId,
  isAffiliateCampaign
}: DefaultTemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { hideApproveRejectInappropriatePostBtn } = useMarketplacePermissions();
  const { isAdvertiser, isAdminStaffAgencyTalentAgencyPartner } = useUserRoles();
  const { handleRejectPost, RejectMarketplacePostModal } = useRejectMarketplacePost();
  const { updating, handleSelectStatus, UpdateMarketplacePostStatusModal } = useUpdateMarketplacePostsStatus({
    marketplaceId
  });
  const { callUpdateMarketplacePostStatusV2, loading: updatingPostStatus } = useUpdateMarketplacePostStatusV2Mutation({
    refetchQueries: [namedOperations.Query.AllMarketplacePosts]
  });
  const { data, loading } = useMarketplacePostQuery({
    variables: { pk: postId },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });
  const { data: historyData, loading: fetchingHistory } = useMarketplacePostHistoryQuery({
    variables: { pk: postId },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });

  const marketplacePost = data?.marketplacePost;

  if (!marketplacePost) {
    return null;
  }

  const {
    images,
    status,
    caption,
    reason,
    postUrl,
    postedDate,
    socialMedia,
    warningReason,
    detailedReason,
    postDetailInfluencer
  } = marketplacePost;
  const hasRequestModificationButton =
    !hideApproveRejectInappropriatePostBtn &&
    (isAdminStaffAgencyTalentAgencyPartner || isAdvertiser) &&
    status &&
    [MarketplaceCampaignPostStatus.EFFECTIVE, MarketplaceCampaignPostStatus.WARNING_SOLVED].includes(status);
  const hasWarningSolvedButton =
    status && status === MarketplaceCampaignPostStatus.WARNING && warningReason === WarningReason.INVALID_CONTENT;
  const hasApproveButton =
    !hideApproveRejectInappropriatePostBtn &&
    !isAffiliateCampaign &&
    status === MarketplaceCampaignPostStatus.EFFECTIVE;

  const handleSetWarningSolvedStatus = async () => {
    try {
      const influencerId = Number(marketplacePost?.postDetailInfluencer.id);

      await callUpdateMarketplacePostStatusV2({
        variables: {
          input: {
            postIds: [postId],
            influencerIds: [influencerId],
            marketplaceId: marketplaceId as number,
            status: MarketplaceCampaignPostStatus.WARNING_SOLVED
          }
        }
      });

      onClose();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <Drawer.Body>
        <RenderDataWithFallback loading={loading || fetchingHistory}>
          <div className="overflow-auto">
            <div className="pb-6">
              <PostInfoSection
                hasImageDownload
                caption={caption}
                postUrl={postUrl}
                postedDate={postedDate}
                socialMedia={socialMedia as keyof typeof SupportedSocialMediaType}
                images={[...(images || [])]}
                influencer={{
                  id: postDetailInfluencer.id,
                  name: postDetailInfluencer.name,
                  avatar: postDetailInfluencer.avatar,
                  followers: postDetailInfluencer.followersCount
                }}
              />

              {detailedReason || reason ? (
                <RequestModificationMessage message={detailedReason || reason} />
              ) : hasRequestModificationButton ? (
                <Button
                  variant="outline"
                  prefixIcon={{ icon: 'send', size: 12 }}
                  title={t('Button.Request modification')}
                  className="mt-4 w-full"
                  onClick={() => handleRejectPost(marketplacePost)}
                />
              ) : null}
            </div>

            <HistorySection data={historyData?.marketplacePostHistory} />

            <RejectMarketplacePostModal />
            <UpdateMarketplacePostStatusModal />
          </div>
        </RenderDataWithFallback>
      </Drawer.Body>

      {hasApproveButton ? (
        <Drawer.Footer>
          <Form.Footer className="sticky bottom-0 z-5 bg-white">
            <p className="max-w-51 text-sm text-tertiary">
              {t('You can send feedback to creator via Email and AnyCreator')}
            </p>
            <Button
              variant="solid"
              loading={updating}
              className={styles.button}
              title={t('Button.Approve Post')}
              onClick={() =>
                status
                  ? handleSelectStatus(status, [
                      {
                        ...marketplacePost,
                        joinedDate: '',
                        revenueItems: [], // don't used here, just to match the MarketplacePost type
                        needReview: false,
                        influencer: marketplacePost.postDetailInfluencer,
                        // TODO: BE improvement, they need to make post status type consistent
                        // On the posts list page BE return the post status as MarketplaceCampaignPostStatusForAdvertiser
                        // But which same post on detail, they return post status as MarketplaceCampaignPostStatus
                        status: marketplacePost.status as unknown as MarketplaceCampaignPostStatusForAdvertiser
                      }
                    ])
                  : undefined
              }
            />
          </Form.Footer>
        </Drawer.Footer>
      ) : hasWarningSolvedButton ? (
        <Drawer.Footer>
          <Form.Footer className="sticky bottom-0 z-5 justify-between bg-white">
            <Button
              variant="solid"
              className={styles.button}
              loading={updatingPostStatus}
              title={t('Button.Make effective')}
              onClick={handleSetWarningSolvedStatus}
            />
          </Form.Footer>
        </Drawer.Footer>
      ) : null}
    </>
  );
};

const styles = {
  button: cn('ml-auto min-w-[unset] px-8 py-0')
};
